<template>
	<div class='step'>
		<!-- Invoice Details block -->
		<InvoiceDetails/>

		<ErrorMessage :message="error_message" v-if='error_message.length > 0' />
		<WarningMessage :message="warning_message" v-if='warning_message.length > 0' />

		<div class='button-box'>
			<div class='row'>
				<div class='col-xs-12 col-lg-4 offset-lg-4 order-last order-lg-first'>
					<div class='contact-text'>
						<b>{{getText('NeedHelp')}}</b> <a class='support-link' :href='"mailto:"+merchant().SupportEmail'>{{merchant().SupportEmail}}</a>
					</div>
				</div>
				<div class='col-xs-12 col-lg-4 order-first order-lg-last'>
					<input class='submit-button' readonly :value="getText('NextStep')" @click="validate()" >
				</div>
			</div>
		</div>

	</div>
</template>

<style scoped lang="scss">
	.button-row{
		margin-top: 30px;
	}
</style>

<script>

import { mapGetters } from "vuex";
import store from "@/store";

import {LOCK_AND_LOAD_DATA } from "@/store/actions.type";

import { SET_INVOICE_DATA } from "@/store/mutations.type";

// Language / Localization
import LanguageService from "@/common/language.service";

// Import Major components
import InvoiceDetails from "@/components/steps/invoice_step/InvoiceDetails";
import PresetInvoiceDetails from "@/components/steps/invoice_step/PresetInvoiceDetails";
import Badge from "@/components/general/Badge";

/* Smaller elements */
import WarningMessage from "@/components/messages/WarningMessage";
import ErrorMessage from "@/components/messages/ErrorMessage";

export default {
	name: "InvoiceStep",
	components: {
		InvoiceDetails,
		PresetInvoiceDetails,
		Badge,

		WarningMessage,
		ErrorMessage,
	},
	data() {
		// Our primary data objects (used to store user data and progress)
		return {
			error_message: "",
			warning_message: "",
			invoice_details: {
				id: this.invoice().id,
				amount: this.invoice().amount,
        type: this.invoice().type,
				payment_country: this.invoice().payment_country,
				currency: this.invoice().payment_currency,
        amount_payable: this.invoice().amount_payable
			}
		}
	},
	beforeMount(){
		var url_params = {
			id: this.invoice().id,
			amount: this.invoice().amount,
			payment_country: this.invoice().payment_country,
			currency: this.invoice().payment_currency,
      type: this.invoice().type,
      amount_payable: this.invoice().amount_payable,
		};
		
		// If a payment country is not yet selected, find a default value for it!
		if(["", null, "null"].includes(this.invoice().payment_country)){
			var new_country = '';
			var default_option = this.merchant().DefaultCountry;
			// If default is defined, figure out what to load
			if(default_option !== undefined){
				if(default_option == "geoip"){
					// Try to set country by user ip
					//@TODO -- At some point get this going
					default_option = "merchant";
				}
				if(default_option == "merchant"){
					// Set merchant's country
					new_country = this.merchant().Country;
				} else {
					// If Default country is a country code we have, set it
					new_country = this.merchant().DefaultCountry;
				}

				// Validate that default country is selectable
				if(this.country_list.includes(new_country)){
					// Its valid, lets set our default country
					url_params.payment_country = new_country;
					store.commit(SET_INVOICE_DATA, url_params);
				}
				
			}
		}

		// If URL params are set, set values from them, then lock those fields
		//// Check and set INVOICE ID from url query param
		if(this.$route.query.invoice !== undefined && this.$route.query.invoice.length > 0) {
			store.dispatch(LOCK_AND_LOAD_DATA, { type: 'invoice', data: this.$route.query.invoice});
		}
		//// Check and set INVOICE AMOUNT from url query param
		if(this.$route.query.amount !== undefined && parseFloat(this.$route.query.amount) > 0 && !isNaN(parseFloat(this.$route.query.amount))) {
			store.dispatch(LOCK_AND_LOAD_DATA, { type: 'amount', data: parseFloat(this.$route.query.amount)});
		}
    //// Check and set INVOICE PAYABLE AMOUNT from url query param
    if(this.$route.query.amountPayable !== undefined && parseFloat(this.$route.query.amountPayable) > 0 && !isNaN(parseFloat(this.$route.query.amountPayable))) {
      store.dispatch(LOCK_AND_LOAD_DATA, { type: 'amountPayable', data: parseFloat(this.$route.query.amountPayable)});
    }
    //// Check and set INVOICE TYPE from url query param
    if(this.$route.query.type !== undefined && this.$route.query.type.length > 0) {
      store.dispatch(LOCK_AND_LOAD_DATA, { type: 'type', data: this.$route.query.type});
    }
    //// Check and set INVOICE currency from url query param
    if(this.$route.query.currency !== undefined && this.$route.query.currency.length > 0) {
      store.dispatch(LOCK_AND_LOAD_DATA, { type: 'currency', data: this.$route.query.currency});
    }
		//// Check and set PAYMENT COUNTRY from url query param
		var found_country = false;
		if(this.$route.query.country !== undefined && this.$route.query.country.length > 0) {
			// Check if Merchant has a rate for selected country
			if(this.country_list.includes(this.$route.query.country)){
				found_country = true;
				store.dispatch(LOCK_AND_LOAD_DATA, { type: 'country', data: this.$route.query.country});
			}
		} else {
			// Set it to trigger
			found_country = true;
			this.invoice_details.payment_country = this.invoice().payment_country;
		}

		// Now that we've loaded URL values & values from /pay, etc. Validate we got the appropriate params
		if(this.merchant().RequireParams){
			if(this.merchant().RequiredUrlParams.Country && !found_country){
				// failed country validation
				this.$router.push({ name: 'Error', params: { error: this.getError('BadUrlMissingCountry') }});
			}
			if(this.merchant().RequiredUrlParams.Amount && !this.invoice().amount){
				// failed amount validation
				this.$router.push({ name: 'Error', params: { error: this.getError('BadUrlMissingAmount') }});
			}
			if(this.merchant().RequiredUrlParams.Invoice && !this.invoice().id){
				// failed invoice validation
				this.$router.push({ name: 'Error', params: { error: this.getError('BadUrlMissingInvoice') }});
			}
		}
	},
	computed: {
		country_list(){
			// Filter out all countries that do not have a supported currency, then format array
			return this.available_countries().map(
				x => x.code
			);
		},
		summary_screen(){
			return 	!this.invoice_editable().payment_country && 
					!this.invoice_editable().invoice && 
					!this.invoice_editable().amount;
		}
	},
	methods: {
		getText(key){
			return LanguageService.getText(key);
		},
		getError(key){
			return LanguageService.getError(key);
		},
		validate: function(){
			this.error_message = "";
			this.warning_message = "";

			// Validate Invoice #
			if(this.invoice().id == "" || String(this.invoice().id).trim().length <= 1) {
				this.warning_message = this.getError('MissingInvoiceId');
				return;
			}
			if(String(this.invoice().id).indexOf(' ') >= 0){
				this.warning_message = this.getError('InvalidInvoiceId');
				return;
			}
			// Validate Invoice Amount
			if(this.invoice().amount == "" || parseFloat(this.invoice().amount) <= 0 || parseFloat(this.invoice().amount) == NaN) {
				this.warning_message = this.getError('AmountInvalid');
				return;
			}

			// Validate Payment Country
			if(!this.invoice().payment_currency) {
				this.warning_message = this.getError('CurrencyInvalid');
				return;
			}

			if(this.rateOffer() === undefined || this.rateOffer().RateOffer === undefined){
				this.error_message = this.getError('NoExchangeRates');
				return;
			}
			/*
			 * END PAYMENT VALIDATION
			 */

			//send a success
			this.$emit('success', true);
		},
		...mapGetters(["invoice", "invoice_editable", "merchant", "badge", "paymentMethods", "rateOffer", "countries", "rates", "available_countries"])
	}
}
</script>
